<script>
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import UnavailabilityCard from './UnavailabilityCard.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CreateUnavailability from './CreateUnavailability.vue';
import { format } from 'date-fns';

export default {
  name: 'StaffDetails',
  components: {
    PageHeader,
    LoadingState,
    ErrorState,
    EmptyState,
    UnavailabilityCard,
    PaginationBar,
    DeleteModal,
    CreateUnavailability,
  },
  data() {
    return {
      displayMode: 'list',
      showCreateModal: false,
      showDeleteModal: false,
      deleteName: null,
      deleteId: null,
      isDeleting: false,
      showCreateModal: false,
      showEditModal: false,
      selectedUnavailablity: null,
      staffDetails: null,
    };
  },
  computed: {
    staffId() {
      return this.$route.params.staffId;
    },
    unavailabilityList() {
      return this.$store.state.staffV2.unavailabilityList || [];
    },
    isLoading() {
      return this.$store.state.staffV2.isLoadingUnavailability || false;
    },
    isError() {
      return this.$store.state.staffV2.isErrorUnavailability || false;
    },
    pagination() {
      return this.$store.getters['staffV2/formattedUnavailabilityPagination'];
    },
    title() {
      if (!this.staffDetails) {
        return 'Staff Management';
      }
      return `Staff Management/${this.staffDetails.staff.name}`;
    },
    staffGender() {
      return this.staffDetails?.staff?.staff_metadata?.gender || null;
    },
    staffSeniority() {
      return this.staffDetails?.staff?.staff_metadata?.seniority || null;
    },
  },
  methods: {
    formatDate(dateString) {
      try {
        return format(new Date(dateString), 'yyyy-MM-dd'); 
      } catch (e) {
        return '-';
      }
    },
    async fetchUnavailability() {
      try {
        await this.$store.dispatch('staffV2/fetchStaffUnavailability', { staffId: this.staffId });
      } catch (err) {
        useAlert(`Failed to fetch unavailability: ${err}`);
      }
    },
    async fetchStaff() {
      try {
        this.staffDetails = await this.$store.dispatch('staffV2/getStaff', this.staffId);
      } catch (err) {
        useAlert(`Failed to fetch staff: ${err}`);
      }
    },
    fetchPreviousPage() {
      if (this.pagination.currentPage > 1) {
        let prev = this.pagination.currentPage - 1;
        this.$store.dispatch('staffV2/updateUnavailabilityPaginationAndFetch', { page: prev });
      }
    },
    fetchNextPage() {
      if (this.pagination.currentPage < this.pagination.totalPages) {
        let next = this.pagination.currentPage + 1;
        this.$store.dispatch('staffV2/updateUnavailabilityPaginationAndFetch', { page: next });
      }
    },
    updatePageSize(newSize) {
      this.$store.dispatch('staffV2/updateUnavailabilityPaginationAndFetch', { page: 1, page_size: newSize });
    },
    handleOpenDelete(id) {
      this.deleteName = 'this unavailability';
      this.deleteId = id;
      this.showDeleteModal = true;
    },
    handleCloseDelete() {
      this.deleteName = null;
      this.deleteId = null;
      this.showDeleteModal = false;
    },
    async handleConfirmDelete() {
      try {
        this.isDeleting = true;
        await this.$store.dispatch('staffV2/deleteStaffUnavailability', { staffId: this.staffId, unavailabilityId: this.deleteId });
        this.isDeleting = false;
        await this.$store.dispatch('staffV2/fetchStaffUnavailability', { staffId: this.staffId });
        useAlert('Unavailability deleted');
        this.handleCloseDelete();
      } catch (err) {
        useAlert(`Failed to delete unavailability: ${err}`);
      } finally {
        this.isDeleting = false;
      }
    },
    handleOpenEdit(item) {
      this.selectedUnavailablity = item;
      this.showEditModal = true;
    },
    handleCloseEdit() {
      this.selectedUnavailablity = null;
      this.showEditModal = false;
    },
  },
  async mounted() {
    await this.fetchUnavailability();
    await this.fetchStaff();
  }
}
</script>

<template>
  <div class="h-full w-full p-4 md:p-8 space-y-6 overflow-y-auto">

    <!-- Modals -->
    <CreateUnavailability v-if="showCreateModal" @close="showCreateModal = false" />
    <CreateUnavailability
      v-if="showEditModal"
      :is-edit="true"
      :unavailability="selectedUnavailablity"
      @close="handleCloseEdit"
    />
    <DeleteModal
      v-if="showDeleteModal"
      :delete-name="deleteName"
      :is-loading="isDeleting"
      @confirm="handleConfirmDelete"
      @close="handleCloseDelete"
    />

    <!-- Page Header -->
    <PageHeader
      :title="title"
      icon="person"
      :allow-back="true"
      description="View and manage a staff member's unavailability records, profile, assigned branches, and services."
    >
      <template #actions>
        <woot-button icon="add" @click="showCreateModal = true">
          Add Unavailability
        </woot-button>
      </template>
    </PageHeader>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- LEFT SIDE -->
      <div class="lg:col-span-2 space-y-6">

        <!-- Staff Details -->
        <div v-if="staffDetails" class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
          <h2 class="text-lg md:text-xl font-semibold mb-4">Staff Details</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div>
                <p class="text-sm text-gray-500 dark:text-gray-400 font-bold">Name</p>
                <p class="font-medium">{{ staffDetails.staff.name }}</p>
              </div>

              <div>
                <p class="text-sm text-gray-500 dark:text-gray-400 font-bold">Status</p>
                <span
                  class="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="staffDetails.staff.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'"
                >
                  {{ staffDetails.staff.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>

              <div v-if="staffGender">
                <p class="text-sm text-gray-500 dark:text-gray-400 font-bold">Gender</p>
                <p>{{ staffGender }}</p>
              </div>
            </div>

            <div class="space-y-4">
              <div v-if="staffSeniority">
                <p class="text-sm text-gray-500 dark:text-gray-400 font-bold">Seniority</p>
                <p>{{ staffSeniority }}</p>
              </div>

              <div>
                <p class="text-sm text-gray-500 dark:text-gray-400 font-bold">Created</p>
                <p>{{ formatDate(staffDetails.staff.created_at) }}</p>
              </div>

              <div>
                <p class="text-sm text-gray-500 dark:text-gray-400 font-bold">Updated</p>
                <p>{{ formatDate(staffDetails.staff.updated_at) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Stats -->
        <div v-if="staffDetails" class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
          <h2 class="text-lg md:text-xl font-semibold mb-4">Summary</h2>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="p-4 rounded-lg bg-slate-50 dark:bg-slate-700 text-center">
              <p class="text-2xl font-bold">{{ staffDetails.stats.total_branches }}</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">Total Branches</p>
            </div>

            <div class="p-4 rounded-lg bg-slate-50 dark:bg-slate-700 text-center">
              <p class="text-2xl font-bold">{{ staffDetails.stats.enabled_branches }}</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">Enabled Branches</p>
            </div>

            <div class="p-4 rounded-lg bg-slate-50 dark:bg-slate-700 text-center">
              <p class="text-2xl font-bold">{{ staffDetails.stats.total_services }}</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">Total Services</p>
            </div>

            <div class="p-4 rounded-lg bg-slate-50 dark:bg-slate-700 text-center">
              <p class="text-2xl font-bold">{{ staffDetails.stats.enabled_services }}</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">Enabled Services</p>
            </div>
          </div>
        </div>

        <!-- Unavailability -->
        <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
          <h2 class="text-lg md:text-xl font-semibold">Unavailability</h2>

          <PaginationBar
            v-if="!isLoading && !isError && unavailabilityList.length"
            :pagination="pagination"
            :loading="isLoading"
            @previous="fetchPreviousPage"
            @next="fetchNextPage"
            @page-size-change="updatePageSize"
          />

          <LoadingState v-if="isLoading" message="Loading unavailability..." />

          <ErrorState v-else-if="isError" />

          <EmptyState
            v-else-if="!isLoading && unavailabilityList.length === 0"
            title="No unavailability found"
            message="Create one to get started."
          />

          <div v-else class="grid gap-4">
            <UnavailabilityCard
              v-for="item in unavailabilityList"
              :key="item.id"
              :unavailability="item"
              @edit="handleOpenEdit"
              @delete="handleOpenDelete"
            />
          </div>
        </div>

      </div>

      <!-- RIGHT SIDE -->
      <div class="space-y-6">

        <!-- Branches -->
        <div v-if="staffDetails" class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
          <h2 class="text-lg md:text-xl font-semibold mb-4">Branches</h2>

          <p v-if="!staffDetails.branches.length" class="text-gray-500 text-center py-4">
            No branches assigned
          </p>

          <ul v-else class="space-y-2 max-h-60 overflow-y-auto">
            <li
              v-for="b in staffDetails.branches"
              :key="b.id"
              class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700 flex justify-between items-center"
            >
              <span>{{ b.name }}</span>
              <span
                :class="[
                  'px-2.5 py-0.5 rounded-full text-xs font-medium',
                  b.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                ]"
              >
                {{ b.is_active ? 'Active' : 'Inactive' }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Services -->
        <div v-if="staffDetails" class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
          <h2 class="text-lg md:text-xl font-semibold mb-4">Services</h2>

          <p v-if="!staffDetails.services.length" class="text-gray-500 text-center py-4">
            No services assigned
          </p>

          <ul v-else class="space-y-2 max-h-60 overflow-y-auto">
            <li
              v-for="s in staffDetails.services"
              :key="s.id"
              class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700"
            >
              {{ s.name }}
            </li>
          </ul>
        </div>

      </div>

    </div>
  </div>
</template>